import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import {
    getCabsList,
    createCab,
    updateCab,
    deleteCab,
    completeCab,
} from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import useVmsList from '../useVmsList';
import VmsListShell from '../components/vms/VmsListShell';
import VmsTable from '../components/vms/VmsTable';
import VmsBadge from '../components/vms/VmsBadge';
import WingFlatPicker from '../components/vms/WingFlatPicker';
import ConfirmDialog from '../components/ConfirmDialog';
import CabFormModal from './CabFormModal';
import { labelCls } from '../components/form/formStyles';
import {
    CAB_STATUSES,
    CAB_TYPES,
    CAB_STATUS_STYLE,
    CAB_TYPE_ICON,
    isCabTerminal,
    fmtDateTime,
    flatLabel,
    humanize,
    toOptions,
    toLocalInput,
} from '../vmsConstants';
import { fmtPhone } from '../visitorConstants';

const STATUS_OPTIONS = toOptions(CAB_STATUSES);
const TYPE_OPTIONS = toOptions(CAB_TYPES);

const Cabs = () => {
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const canCreate = can(PERMISSIONS.VMS_CAB_CREATE);
    const canUpdate = can(PERMISSIONS.VMS_CAB_UPDATE);
    const canComplete = can(PERMISSIONS.VMS_CAB_COMPLETE);

    const { saving, acting } = useSelector(state => state.VmsReducer?.cabs) || {};

    const [formTarget, setFormTarget] = useState(null);
    const [completeTarget, setCompleteTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const list = useVmsList({
        slice: 'cabs',
        action: getCabsList,
        filters: { status: '', cabType: '', wingId: '', flatId: '' },
    });

    const closeForm = () => setFormTarget(null);

    const handleSave = (payload) => {
        const done = () => { closeForm(); list.refresh(); };
        if (formTarget.mode === 'create') {
            dispatch(createCab({ ...payload, onSuccess: done }));
        } else {
            dispatch(updateCab({ id: formTarget.cab.cab_id, ...payload, onSuccess: done }));
        }
    };

    const handleComplete = () => {
        dispatch(completeCab({
            id: completeTarget.cab_id,
            onSuccess: () => { setCompleteTarget(null); list.refresh(); },
        }));
    };

    const handleDelete = () => {
        dispatch(deleteCab({
            id: deleteTarget.cab_id,
            onSuccess: () => { setDeleteTarget(null); list.refresh(); },
        }));
    };

    const columns = [
        {
            key: 'cab_code',
            header: 'Cab',
            nowrap: true,
            render: (c) => (
                <div>
                    <p className="tw-mb-0 tw-font-mono tw-font-semibold tw-text-gray-800">{c.cab_code || '—'}</p>
                    <p className="tw-mb-0 tw-mt-0.5 tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-500">
                        <Icon icon={CAB_TYPE_ICON[c.cab_type] || CAB_TYPE_ICON.OTHER} />
                        {humanize(c.cab_type)}
                    </p>
                </div>
            ),
        },
        { key: 'flat', header: 'Flat', nowrap: true, render: (c) => flatLabel(c) },
        {
            key: 'driver',
            header: 'Driver',
            render: (c) => (
                <div className="tw-min-w-0">
                    <p className="tw-mb-0 tw-font-semibold tw-text-gray-800 tw-truncate">
                        {c.driver_name || c.provider_name || '—'}
                    </p>
                    <p className="tw-mb-0 tw-mt-0.5 tw-text-[10px] tw-text-gray-500 tw-truncate">
                        {c.provider_name && c.driver_name ? `${c.provider_name} · ` : ''}
                        {c.driver_phone ? fmtPhone(c.driver_phone) : ''}
                    </p>
                </div>
            ),
        },
        {
            key: 'vehicle',
            header: 'Vehicle',
            render: (c) => (
                c.vehicle_number
                    ? (
                        <div className="tw-min-w-0">
                            <p className="tw-mb-0 tw-font-mono tw-text-gray-800">{c.vehicle_number}</p>
                            <p className="tw-mb-0 tw-mt-0.5 tw-text-[10px] tw-text-gray-500 tw-truncate">
                                {[c.vehicle_model, c.vehicle_type && humanize(c.vehicle_type)].filter(Boolean).join(' · ')}
                            </p>
                        </div>
                    )
                    : <span className="tw-text-gray-300">—</span>
            ),
        },
        {
            key: 'timing',
            header: 'Expected / Left',
            nowrap: true,
            render: (c) => (
                <div className="tw-text-[10px] tw-leading-snug">
                    <p className="tw-mb-0">{c.expected_at ? fmtDateTime(c.expected_at) : <span className="tw-text-gray-300">—</span>}</p>
                    <p className="tw-mb-0 tw-text-gray-500">{c.departed_at ? fmtDateTime(c.departed_at) : '—'}</p>
                </div>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (c) => <VmsBadge value={c.status} styles={CAB_STATUS_STYLE} />,
        },
        {
            key: 'actions',
            header: 'Action',
            align: 'right',
            stopPropagation: true,
            render: (c) => {
                const terminal = isCabTerminal(c);
                return (
                    <div className="tw-inline-flex tw-items-center tw-gap-1">
                        {/* Complete is unguarded server-side — calling it on an
                            already-completed trip succeeds and overwrites
                            departed_at. Hiding the button is the only guard. */}
                        {canComplete && !terminal && (
                            <button
                                title="Mark completed"
                                disabled={acting}
                                onClick={() => setCompleteTarget(c)}
                                className="tw-p-1.5 tw-rounded hover:tw-bg-emerald-50 tw-text-emerald-600 disabled:tw-opacity-50"
                            >
                                <Icon icon="mdi:car-check" className="tw-text-base" />
                            </button>
                        )}
                        {canUpdate && (
                            <button
                                title="Edit"
                                onClick={() => setFormTarget({ mode: 'edit', cab: c })}
                                className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                            >
                                <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                            </button>
                        )}
                        {canUpdate && (
                            <button
                                title="Remove"
                                disabled={acting}
                                onClick={() => setDeleteTarget(c)}
                                className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600 disabled:tw-opacity-50"
                            >
                                <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                            </button>
                        )}
                    </div>
                );
            },
        },
    ];

    const filters = (
        <>
            <div>
                <label className={labelCls}>Status</label>
                <SearchableDropdown
                    small
                    value={list.filters.status}
                    onChange={(v) => list.setFilter('status', v === 'all' ? '' : v)}
                    options={STATUS_OPTIONS}
                    placeholder="All statuses"
                />
            </div>
            <div>
                <label className={labelCls}>Trip type</label>
                <SearchableDropdown
                    small
                    value={list.filters.cabType}
                    onChange={(v) => list.setFilter('cabType', v === 'all' ? '' : v)}
                    options={TYPE_OPTIONS}
                    placeholder="All types"
                />
            </div>
            <WingFlatPicker
                clearable
                required={false}
                societyId={list.societyId}
                wingId={list.filters.wingId}
                flatId={list.filters.flatId}
                onWingChange={(v) => list.setFilter('wingId', v)}
                onFlatChange={(v) => list.setFilter('flatId', v)}
                wingPlaceholder="Any wing"
                flatPlaceholder="Any flat"
            />
        </>
    );

    const editInitial = formTarget?.cab ? {
        cab_type: formTarget.cab.cab_type || 'PICKUP',
        provider_name: formTarget.cab.provider_name || '',
        driver_name: formTarget.cab.driver_name || '',
        driver_phone: formTarget.cab.driver_phone || '',
        vehicle_number: formTarget.cab.vehicle_number || '',
        vehicle_type: formTarget.cab.vehicle_type || '',
        vehicle_model: formTarget.cab.vehicle_model || '',
        booking_id: formTarget.cab.booking_id || '',
        status: formTarget.cab.status || 'EXPECTED',
        expected_at: toLocalInput(formTarget.cab.expected_at),
        entry_gate: formTarget.cab.entry_gate || '',
        exit_gate: formTarget.cab.exit_gate || '',
        notes: formTarget.cab.notes || '',
        cab_code: formTarget.cab.cab_code,
        flatLabel: flatLabel(formTarget.cab),
    } : null;

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <VmsListShell
                title="Cabs"
                subtitle="Pickups and drops at the gate"
                itemLabel="cab logs"
                list={list}
                filters={filters}
                headerActions={canCreate && (
                    <button
                        onClick={() => setFormTarget({ mode: 'create' })}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700"
                    >
                        <Icon icon="mdi:taxi" className="tw-text-base" />
                        Log Cab
                    </button>
                )}
            >
                <VmsTable
                    columns={columns}
                    rows={list.rows}
                    loading={list.loading}
                    loaderLabel="Loading cabs..."
                    rowKey={(c) => c.cab_id}
                    empty={{
                        icon: 'mdi:taxi',
                        filtered: list.hasFilters,
                        filteredTitle: 'No cabs match these filters',
                        emptyTitle: 'No cabs logged yet',
                        emptyHint: 'Log a pickup or drop and the flat’s residents are notified.',
                        onClear: list.resetFilters,
                    }}
                />
            </VmsListShell>

            <CabFormModal
                open={!!formTarget}
                mode={formTarget?.mode || 'create'}
                societyId={list.societyId}
                initial={editInitial}
                submitting={saving}
                onSubmit={handleSave}
                onCancel={closeForm}
            />

            <ConfirmDialog
                open={!!completeTarget}
                title="Mark this trip completed?"
                subtitle={completeTarget?.cab_code}
                confirmLabel="Mark completed"
                busyLabel="Saving..."
                tone="success"
                icon="mdi:car-check"
                busy={acting}
                onCancel={() => setCompleteTarget(null)}
                onConfirm={handleComplete}
            >
                <p className="tw-mb-0">
                    The departure time is stamped now, and the residents of {flatLabel(completeTarget || {})} are notified.
                </p>
            </ConfirmDialog>

            <ConfirmDialog
                open={!!deleteTarget}
                title="Remove this cab log?"
                subtitle="The record is hidden from the list."
                confirmLabel="Remove"
                busyLabel="Removing..."
                tone="danger"
                icon="mdi:trash-can-outline"
                busy={acting}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
            >
                <p className="tw-mb-0">
                    <span className="tw-font-mono tw-font-semibold">{deleteTarget?.cab_code}</span> for{' '}
                    {flatLabel(deleteTarget || {})} will be removed.
                </p>
            </ConfirmDialog>
        </div>
    );
};

export default Cabs;
